import React, { useState, useEffect, useCallback } from "react";
import {
    getCommissionRuns,
    generateCommissionRuns,
    toggleCommissionLine,
    updateCommissionRun,
    finalizeCommissionRun,
    unfinalizeCommissionRun,
    getMyCommissionRun,
    CommissionRun,
    CommissionRunsListItem,
} from "../services/api";
import { useUserProfile } from "../hooks/useUserProfile";

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const primaryBtnStyle: React.CSSProperties = {
    backgroundColor: "#F18E00", color: "white", border: "none", borderRadius: "4px",
    padding: "10px 20px", cursor: "pointer", fontSize: "13px", fontWeight: "bold",
};
const secondaryBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#5C5D5E", border: "1px solid #ddd",
    borderRadius: "4px", padding: "10px 20px", cursor: "pointer", fontSize: "13px",
};
const smallBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#5C5D5E", border: "1px solid #ddd",
    borderRadius: "4px", padding: "5px 12px", cursor: "pointer", fontSize: "12px",
};
const dangerBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#C00000", border: "1px solid #f0c0c0",
    borderRadius: "4px", padding: "5px 12px", cursor: "pointer", fontSize: "12px",
};
const cardStyle: React.CSSProperties = {
    border: "1px solid #e0e0e0", borderRadius: "8px", backgroundColor: "white",
    marginBottom: "16px", overflow: "hidden",
};
const fieldLabelStyle: React.CSSProperties = {
    fontSize: "11px", color: "#999", textTransform: "uppercase", letterSpacing: "0.03em",
};
const inputStyle: React.CSSProperties = {
    padding: "6px 8px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px",
    width: "120px", boxSizing: "border-box",
};

function defaultPeriod(): string {
    // Commission timing is month M paid in month M+1's payroll, so the most
    // useful default view is last month's hours.
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const money = (n: number): string =>
    n.toLocaleString(undefined, { style: "currency", currency: "USD" });

const hrsFmt = (n: number): string =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface ManualFields {
    prior_year_december_netting: string;
    payout_override: string;
    customer_commission_amount: string;
    customer_commission_payroll_month: string;
}

const toManualFields = (run: CommissionRun): ManualFields => ({
    prior_year_december_netting: run.prior_year_december_netting ? String(run.prior_year_december_netting) : "",
    payout_override: run.payout_override !== null ? String(run.payout_override) : "",
    customer_commission_amount: run.customer_commission_amount !== null ? String(run.customer_commission_amount) : "",
    customer_commission_payroll_month: run.customer_commission_payroll_month || "",
});

// Full read/write module -- generate/refresh runs from Zoho, toggle lines,
// edit manual fields, finalize/reopen. Restricted to hr_payroll/admin on
// the backend (get_current_hr_or_admin_user); rendered here only when the
// signed-in user holds one of those roles (see the CommissionHours
// wrapper component at the bottom of this file).
const AdminCommissionHours: React.FC = () => {
    const [period, setPeriod] = useState(defaultPeriod());
    const [employees, setEmployees] = useState<CommissionRunsListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [generating, setGenerating] = useState(false);
    const [excludedTotal, setExcludedTotal] = useState<number | null>(null);
    const [expanded, setExpanded] = useState<Record<number, boolean>>({});
    const [manualDrafts, setManualDrafts] = useState<Record<number, ManualFields>>({});
    const [savingRunId, setSavingRunId] = useState<number | null>(null);

    const [yearStr, monthStr] = period.split("-");
    const year = Number(yearStr);
    const month = Number(monthStr);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getCommissionRuns(year, month);
            setEmployees(data.employees);
            const drafts: Record<number, ManualFields> = {};
            data.employees.forEach(item => {
                if (item.run) drafts[item.run.id] = toManualFields(item.run);
            });
            setManualDrafts(drafts);
        } catch (e: any) {
            console.error("Failed to load commission runs", e);
            setError(e?.response?.data?.detail || "Failed to load commission runs. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [year, month]);

    useEffect(() => {
        load();
    }, [load]);

    const updateRunInState = (run: CommissionRun) => {
        setEmployees(prev => prev.map(item => (item.plan_id === run.plan_id ? { ...item, run } : item)));
        setManualDrafts(prev => ({ ...prev, [run.id]: toManualFields(run) }));
    };

    const handleGenerate = async (regenerate: boolean) => {
        if (regenerate && !window.confirm(
            "Regenerating will pull fresh entries from Zoho and reset any lines you've toggled off for this month. Continue?"
        )) {
            return;
        }
        setGenerating(true);
        setError(null);
        try {
            const result = await generateCommissionRuns(year, month, regenerate);
            setExcludedTotal(result.excluded_hours_total);
            await load();
        } catch (e: any) {
            console.error("Failed to generate commission runs", e);
            setError(e?.response?.data?.detail || "Failed to generate commission runs. Please try again.");
        } finally {
            setGenerating(false);
        }
    };

    const handleToggleLine = async (run: CommissionRun, lineId: number, included: boolean) => {
        try {
            const updated = await toggleCommissionLine(run.id, lineId, included);
            updateRunInState(updated);
        } catch (e: any) {
            console.error("Failed to update line", e);
            setError(e?.response?.data?.detail || "Failed to update that line. Please try again.");
        }
    };

    const handleManualFieldChange = (runId: number, field: keyof ManualFields, value: string) => {
        setManualDrafts(prev => ({ ...prev, [runId]: { ...prev[runId], [field]: value } }));
    };

    const handleSaveManualFields = async (run: CommissionRun) => {
        const draft = manualDrafts[run.id];
        if (!draft) return;
        setSavingRunId(run.id);
        setError(null);
        try {
            const payload: any = {
                prior_year_december_netting: draft.prior_year_december_netting === "" ? 0 : Number(draft.prior_year_december_netting),
                customer_commission_amount: draft.customer_commission_amount === "" ? null : Number(draft.customer_commission_amount),
                customer_commission_payroll_month: draft.customer_commission_payroll_month || null,
            };
            if (draft.payout_override === "") {
                payload.clear_payout_override = true;
            } else {
                payload.payout_override = Number(draft.payout_override);
            }
            const updated = await updateCommissionRun(run.id, payload);
            updateRunInState(updated);
        } catch (e: any) {
            console.error("Failed to save commission fields", e);
            setError(e?.response?.data?.detail || "Failed to save. Please try again.");
        } finally {
            setSavingRunId(null);
        }
    };

    const handleFinalize = async (run: CommissionRun) => {
        if (!window.confirm(
            `Finalize ${run.employee_name}'s ${MONTH_NAMES[run.month - 1]} ${run.year} commission run? ` +
            "This locks it from further edits."
        )) {
            return;
        }
        try {
            const updated = await finalizeCommissionRun(run.id);
            updateRunInState(updated);
        } catch (e: any) {
            console.error("Failed to finalize run", e);
            setError(e?.response?.data?.detail || "Failed to finalize. Please try again.");
        }
    };

    const handleUnfinalize = async (run: CommissionRun) => {
        if (!window.confirm("Reopen this run for editing?")) return;
        try {
            const updated = await unfinalizeCommissionRun(run.id);
            updateRunInState(updated);
        } catch (e: any) {
            console.error("Failed to reopen run", e);
            setError(e?.response?.data?.detail || "Failed to reopen. Please try again.");
        }
    };

    const toggleExpanded = (planId: number) => {
        setExpanded(prev => ({ ...prev, [planId]: !prev[planId] }));
    };

    const renderRun = (item: CommissionRunsListItem) => {
        const run = item.run;
        const isExpanded = !!expanded[item.plan_id];

        if (!run) {
            return (
                <div key={item.plan_id} style={cardStyle}>
                    <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ fontWeight: "bold", fontSize: "14px", color: "#5C5D5E" }}>{item.employee_name}</div>
                        <div style={{ fontSize: "12px", color: "#999" }}>No run generated yet for this month.</div>
                    </div>
                </div>
            );
        }

        const draft = manualDrafts[run.id] || toManualFields(run);
        const finalized = run.status === "finalized";
        const diffOk = Math.abs(run.calculated_diff) < 0.005;

        return (
            <div key={item.plan_id} style={cardStyle}>
                <div
                    style={{
                        padding: "16px 20px", display: "flex", justifyContent: "space-between",
                        alignItems: "center", cursor: "pointer", backgroundColor: "#FAFAFA",
                    }}
                    onClick={() => toggleExpanded(item.plan_id)}
                >
                    <div>
                        <div style={{ fontWeight: "bold", fontSize: "14px", color: "#5C5D5E" }}>
                            {run.employee_name}
                            {finalized && (
                                <span style={{
                                    marginLeft: "10px", fontSize: "11px", fontWeight: "bold", color: "#2E7D32",
                                    backgroundColor: "#E8F5E9", padding: "2px 8px", borderRadius: "10px",
                                }}>
                                    FINALIZED
                                </span>
                            )}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                            {hrsFmt(run.included_hours)} hrs included · Cumulative {hrsFmt(run.cumulative_hours_before)} → {hrsFmt(run.cumulative_hours_after)} hrs this year
                        </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: "18px", fontWeight: "bold", color: "#F18E00" }}>{money(run.final_payout)}</div>
                        <div style={{ fontSize: "11px", color: "#999" }}>final payroll payout</div>
                    </div>
                </div>

                {isExpanded && (
                    <div style={{ padding: "0 20px 20px 20px", borderTop: "1px solid #f0f0f0" }}>
                        {/* Line entries */}
                        <div style={{ marginTop: "16px", overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                <thead>
                                    <tr style={{ textAlign: "left", color: "#999", fontSize: "11px", textTransform: "uppercase" }}>
                                        <th style={{ padding: "6px 8px" }}>Include</th>
                                        <th style={{ padding: "6px 8px" }}>Date</th>
                                        <th style={{ padding: "6px 8px" }}>Project</th>
                                        <th style={{ padding: "6px 8px" }}>Task</th>
                                        <th style={{ padding: "6px 8px" }}>Notes</th>
                                        <th style={{ padding: "6px 8px", textAlign: "right" }}>Hours</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {run.lines.length === 0 && (
                                        <tr>
                                            <td colSpan={6} style={{ padding: "12px 8px", color: "#999" }}>No time entries this month.</td>
                                        </tr>
                                    )}
                                    {run.lines.map(line => (
                                        <tr key={line.id} style={{
                                            borderTop: "1px solid #f0f0f0",
                                            opacity: line.included ? 1 : 0.4,
                                        }}>
                                            <td style={{ padding: "6px 8px" }}>
                                                <input
                                                    type="checkbox"
                                                    checked={line.included}
                                                    disabled={finalized}
                                                    onChange={e => handleToggleLine(run, line.id, e.target.checked)}
                                                />
                                            </td>
                                            <td style={{ padding: "6px 8px" }}>{line.date}</td>
                                            <td style={{ padding: "6px 8px" }}>{line.project}</td>
                                            <td style={{ padding: "6px 8px" }}>{line.task}</td>
                                            <td style={{ padding: "6px 8px", color: "#666" }}>{line.notes}</td>
                                            <td style={{ padding: "6px 8px", textAlign: "right" }}>{hrsFmt(line.hours)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr style={{ borderTop: "2px solid #ddd", fontWeight: "bold" }}>
                                        <td colSpan={5} style={{ padding: "8px", textAlign: "right" }}>Included total:</td>
                                        <td style={{ padding: "8px", textAlign: "right" }}>{hrsFmt(run.included_hours)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {!finalized && (
                            <div style={{ marginTop: "8px" }}>
                                <button
                                    style={smallBtnStyle}
                                    onClick={() => handleGenerate(true)}
                                    disabled={generating}
                                >
                                    Refresh entries from Zoho
                                </button>
                            </div>
                        )}

                        {/* Payout summary */}
                        <div style={{
                            marginTop: "20px", padding: "16px", backgroundColor: "#FFF8EC",
                            borderRadius: "6px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                            gap: "16px",
                        }}>
                            <div>
                                <div style={fieldLabelStyle}>Tiered bonus this month</div>
                                <div style={{ fontSize: "15px", color: "#5C5D5E" }}>{money(run.tier_payout)}</div>
                            </div>
                            <div>
                                <div style={fieldLabelStyle}>Prior-Dec netting</div>
                                <input
                                    style={inputStyle}
                                    type="number"
                                    step="0.01"
                                    disabled={finalized}
                                    value={draft.prior_year_december_netting}
                                    onChange={e => handleManualFieldChange(run.id, "prior_year_december_netting", e.target.value)}
                                />
                            </div>
                            <div>
                                <div style={fieldLabelStyle}>Payout override</div>
                                <input
                                    style={inputStyle}
                                    type="number"
                                    step="0.01"
                                    placeholder={money(run.computed_final_payout)}
                                    disabled={finalized}
                                    value={draft.payout_override}
                                    onChange={e => handleManualFieldChange(run.id, "payout_override", e.target.value)}
                                />
                            </div>
                            <div>
                                <div style={fieldLabelStyle}>Calculated diff</div>
                                <div style={{ fontSize: "15px", color: diffOk ? "#2E7D32" : "#C00000", fontWeight: "bold" }}>
                                    {money(run.calculated_diff)}
                                </div>
                            </div>
                        </div>

                        {/* Customer commission (manual, invoice-driven) */}
                        <div style={{ marginTop: "16px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px" }}>
                            <div>
                                <div style={fieldLabelStyle}>
                                    Customer commission ({run.customer_commission_pct ?? 0}%{run.customer_commission_customer ? ` on ${run.customer_commission_customer}` : ""})
                                </div>
                                <input
                                    style={inputStyle}
                                    type="number"
                                    step="0.01"
                                    placeholder="$ amount"
                                    disabled={finalized}
                                    value={draft.customer_commission_amount}
                                    onChange={e => handleManualFieldChange(run.id, "customer_commission_amount", e.target.value)}
                                />
                            </div>
                            <div>
                                <div style={fieldLabelStyle}>Assign to payroll month</div>
                                <input
                                    style={inputStyle}
                                    type="month"
                                    disabled={finalized}
                                    value={draft.customer_commission_payroll_month}
                                    onChange={e => handleManualFieldChange(run.id, "customer_commission_payroll_month", e.target.value)}
                                />
                            </div>
                        </div>

                        <div style={{ marginTop: "18px", display: "flex", gap: "10px", alignItems: "center" }}>
                            {!finalized && (
                                <>
                                    <button
                                        style={secondaryBtnStyle}
                                        onClick={() => handleSaveManualFields(run)}
                                        disabled={savingRunId === run.id}
                                    >
                                        {savingRunId === run.id ? "Saving…" : "Save"}
                                    </button>
                                    <button style={primaryBtnStyle} onClick={() => handleFinalize(run)}>
                                        Finalize for Payroll
                                    </button>
                                </>
                            )}
                            {finalized && (
                                <>
                                    <span style={{ fontSize: "12px", color: "#999" }}>
                                        Finalized {run.finalized_at ? new Date(run.finalized_at).toLocaleString() : ""}
                                    </span>
                                    <button style={dangerBtnStyle} onClick={() => handleUnfinalize(run)}>
                                        Reopen for editing
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            <h1 style={{ color: "#5C5D5E", margin: "0 0 6px 0", fontSize: "22px" }}>Commission Hours</h1>
            <p style={{ color: "#666", marginTop: 0, marginBottom: "20px", fontSize: "13px" }}>
                Pick a month, review each consultant's Zoho-logged hours, toggle off anything that shouldn't
                count, then finalize once the payout is ready to key into payroll.
            </p>

            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
                <input
                    type="month"
                    value={period}
                    onChange={e => setPeriod(e.target.value)}
                    style={{ ...inputStyle, width: "160px" }}
                />
                <button style={primaryBtnStyle} onClick={() => handleGenerate(false)} disabled={generating || loading}>
                    {generating ? "Pulling from Zoho…" : "Generate / Refresh Runs"}
                </button>
                {excludedTotal !== null && (
                    <span style={{ fontSize: "12px", color: "#999" }}>
                        ({hrsFmt(excludedTotal)} hrs excluded-project hours dropped)
                    </span>
                )}
            </div>

            {error && <div style={{ color: "#C00000", fontSize: "13px", marginBottom: "16px" }}>{error}</div>}

            {loading && <div style={{ color: "#666" }}>Loading...</div>}

            {!loading && employees.length === 0 && !error && (
                <div style={{ color: "#999", fontSize: "13px" }}>
                    No commission plans are configured yet.
                </div>
            )}

            {!loading && employees.map(renderRun)}
        </div>
    );
};

// ---------------------------------------------------------------------------
// View-only, self-scoped module -- for anyone holding the "consultant" role.
// Shows only the signed-in user's own commission data for one month at a
// time: no line-toggle checkboxes, no manual-field inputs, no generate/
// refresh/finalize/unfinalize controls. Backed by GET /commissions/my-run,
// which the backend restricts to the caller's own linked plan and always
// denies to subcontractors (see get_current_commission_viewer_user).
// ---------------------------------------------------------------------------
const SelfCommissionHours: React.FC = () => {
    const [period, setPeriod] = useState(defaultPeriod());
    const [hasPlan, setHasPlan] = useState(true);
    const [employeeName, setEmployeeName] = useState("");
    const [run, setRun] = useState<CommissionRun | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [yearStr, monthStr] = period.split("-");
    const year = Number(yearStr);
    const month = Number(monthStr);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getMyCommissionRun(year, month);
            setHasPlan(data.has_plan);
            setEmployeeName(data.employee_name);
            setRun(data.run);
        } catch (e: any) {
            console.error("Failed to load commission hours", e);
            const detail = e?.response?.data?.detail;
            setError(typeof detail === "string" && detail.trim() ? detail : "Failed to load your commission hours. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [year, month]);

    useEffect(() => {
        load();
    }, [load]);

    const finalized = run?.status === "finalized";

    return (
        <div>
            <h1 style={{ color: "#5C5D5E", margin: "0 0 6px 0", fontSize: "22px" }}>Commission Hours</h1>
            <p style={{ color: "#666", marginTop: 0, marginBottom: "20px", fontSize: "13px" }}>
                A view-only summary of your own Zoho-logged commission hours by month.
            </p>

            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
                <input
                    type="month"
                    value={period}
                    onChange={e => setPeriod(e.target.value)}
                    style={{ ...inputStyle, width: "160px" }}
                />
            </div>

            {error && <div style={{ color: "#C00000", fontSize: "13px", marginBottom: "16px" }}>{error}</div>}

            {loading && <div style={{ color: "#666" }}>Loading...</div>}

            {!loading && !error && !hasPlan && (
                <div style={{ color: "#999", fontSize: "13px" }}>
                    No commission plan is linked to your account yet. Contact your administrator if you
                    believe this is a mistake.
                </div>
            )}

            {!loading && !error && hasPlan && !run && (
                <div style={cardStyle}>
                    <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ fontWeight: "bold", fontSize: "14px", color: "#5C5D5E" }}>{employeeName}</div>
                        <div style={{ fontSize: "12px", color: "#999" }}>No run generated yet for this month.</div>
                    </div>
                </div>
            )}

            {!loading && !error && run && (
                <div style={cardStyle}>
                    <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#FAFAFA" }}>
                        <div>
                            <div style={{ fontWeight: "bold", fontSize: "14px", color: "#5C5D5E" }}>
                                {run.employee_name}
                                {finalized && (
                                    <span style={{
                                        marginLeft: "10px", fontSize: "11px", fontWeight: "bold", color: "#2E7D32",
                                        backgroundColor: "#E8F5E9", padding: "2px 8px", borderRadius: "10px",
                                    }}>
                                        FINALIZED
                                    </span>
                                )}
                            </div>
                            <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                                {hrsFmt(run.included_hours)} hrs included · Cumulative {hrsFmt(run.cumulative_hours_before)} → {hrsFmt(run.cumulative_hours_after)} hrs this year
                            </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: "18px", fontWeight: "bold", color: "#F18E00" }}>{money(run.final_payout)}</div>
                            <div style={{ fontSize: "11px", color: "#999" }}>final payroll payout</div>
                        </div>
                    </div>

                    <div style={{ padding: "0 20px 20px 20px", borderTop: "1px solid #f0f0f0" }}>
                        <div style={{ marginTop: "16px", overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                <thead>
                                    <tr style={{ textAlign: "left", color: "#999", fontSize: "11px", textTransform: "uppercase" }}>
                                        <th style={{ padding: "6px 8px" }}>Date</th>
                                        <th style={{ padding: "6px 8px" }}>Project</th>
                                        <th style={{ padding: "6px 8px" }}>Task</th>
                                        <th style={{ padding: "6px 8px" }}>Notes</th>
                                        <th style={{ padding: "6px 8px", textAlign: "right" }}>Hours</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {run.lines.length === 0 && (
                                        <tr>
                                            <td colSpan={5} style={{ padding: "12px 8px", color: "#999" }}>No time entries this month.</td>
                                        </tr>
                                    )}
                                    {run.lines.map(line => (
                                        <tr key={line.id} style={{ borderTop: "1px solid #f0f0f0", opacity: line.included ? 1 : 0.4 }}>
                                            <td style={{ padding: "6px 8px" }}>{line.date}</td>
                                            <td style={{ padding: "6px 8px" }}>{line.project}</td>
                                            <td style={{ padding: "6px 8px" }}>{line.task}</td>
                                            <td style={{ padding: "6px 8px", color: "#666" }}>{line.notes}</td>
                                            <td style={{ padding: "6px 8px", textAlign: "right" }}>{hrsFmt(line.hours)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr style={{ borderTop: "2px solid #ddd", fontWeight: "bold" }}>
                                        <td colSpan={4} style={{ padding: "8px", textAlign: "right" }}>Included total:</td>
                                        <td style={{ padding: "8px", textAlign: "right" }}>{hrsFmt(run.included_hours)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <div style={{
                            marginTop: "20px", padding: "16px", backgroundColor: "#FFF8EC",
                            borderRadius: "6px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                            gap: "16px",
                        }}>
                            <div>
                                <div style={fieldLabelStyle}>Tiered bonus this month</div>
                                <div style={{ fontSize: "15px", color: "#5C5D5E" }}>{money(run.tier_payout)}</div>
                            </div>
                            <div>
                                <div style={fieldLabelStyle}>Prior-Dec netting</div>
                                <div style={{ fontSize: "15px", color: "#5C5D5E" }}>{money(run.prior_year_december_netting)}</div>
                            </div>
                            <div>
                                <div style={fieldLabelStyle}>Payout override</div>
                                <div style={{ fontSize: "15px", color: "#5C5D5E" }}>
                                    {run.payout_override !== null ? money(run.payout_override) : "—"}
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: "16px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px" }}>
                            <div>
                                <div style={fieldLabelStyle}>
                                    Customer commission ({run.customer_commission_pct ?? 0}%{run.customer_commission_customer ? ` on ${run.customer_commission_customer}` : ""})
                                </div>
                                <div style={{ fontSize: "15px", color: "#5C5D5E" }}>
                                    {run.customer_commission_amount !== null ? money(run.customer_commission_amount) : "—"}
                                </div>
                            </div>
                            <div>
                                <div style={fieldLabelStyle}>Assign to payroll month</div>
                                <div style={{ fontSize: "15px", color: "#5C5D5E" }}>{run.customer_commission_payroll_month || "—"}</div>
                            </div>
                        </div>

                        {finalized && (
                            <div style={{ marginTop: "18px" }}>
                                <span style={{ fontSize: "12px", color: "#999" }}>
                                    Finalized {run.finalized_at ? new Date(run.finalized_at).toLocaleString() : ""}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Wrapper: renders the full read/write module for hr_payroll/admin, or the
// view-only self-scoped module for anyone else with access (currently
// consultant). Subcontractors never see the nav link (see Sidebar.tsx), and
// if they reach this route directly the backend rejects the self-view
// endpoint outright, surfacing as the error state above.
// ---------------------------------------------------------------------------
const CommissionHours: React.FC = () => {
    const { roles, loading } = useUserProfile();

    if (loading) {
        return <div style={{ color: "#666" }}>Loading...</div>;
    }

    const isFullAccess = roles.includes("hr_payroll") || roles.includes("admin");
    return isFullAccess ? <AdminCommissionHours /> : <SelfCommissionHours />;
};

export default CommissionHours;
